import * as React from "react";
import { Megaphone, Star } from "lucide-react";
import { RadioGroup, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { DesignBasis } from "../NewDesignPage";

interface PostKindPickerProps {
  value: DesignBasis;
  onChange(basis: DesignBasis): void;
  canCreateReviewDesigns: boolean;
  disabled?: boolean;
}

const OPTIONS: { value: DesignBasis; icon: typeof Star; title: string; help: string }[] = [
  {
    value: "review",
    icon: Star,
    title: "A client review",
    help: "Filled with each player's own reviews when they share it",
  },
  {
    value: "other",
    icon: Megaphone,
    title: "Your own message",
    help: "Rates, open houses, tips, introductions",
  },
];

export const PostKindPicker: React.FC<PostKindPickerProps> = ({
  value,
  onChange,
  canCreateReviewDesigns,
  disabled,
}) => (
  <div className="flex flex-col gap-2">
    <RadioGroup
      value={value}
      onValueChange={next => onChange(next as DesignBasis)}
      disabled={disabled}
      aria-label="What's this design built around?"
      className="grid grid-cols-1 sm:grid-cols-2 gap-3"
    >
      {OPTIONS.map(option => {
        const Icon = option.icon;
        const optionDisabled = disabled || (option.value === "review" && !canCreateReviewDesigns);
        const selected = value === option.value;
        const card = (
          <label
            data-testid={`basis-${option.value}`}
            data-selected={selected ? "true" : "false"}
            className={cn(
              "flex items-start gap-3 rounded-2xl border bg-white p-4 shadow-sm transition-colors h-full",
              "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/50",
              selected ? "border-primary ring-2 ring-primary/20" : "border-slate-200",
              optionDisabled ? "cursor-default opacity-50" : "cursor-pointer hover:border-slate-300"
            )}
          >
            {/* Wrapped so the visually hidden radio sits outside the flex row and adds no gap. */}
            <span className="sr-only">
              <RadioGroup.Item value={option.value} disabled={optionDisabled} aria-label={option.title} />
            </span>
            <span className="bg-indigo-50/50 text-primary p-1.5 rounded-lg shrink-0">
              <Icon className="size-4" aria-hidden="true" />
            </span>
            <span className="flex flex-col gap-0.5 min-w-0">
              <span className="text-sm font-semibold text-slate-800">{option.title}</span>
              <span className="text-xs text-slate-500">{option.help}</span>
            </span>
          </label>
        );

        if (option.value === "review" && !canCreateReviewDesigns) {
          return (
            <Tooltip key={option.value}>
              <Tooltip.Trigger render={<span className="flex" data-testid="basis-review-tooltip" />}>
                {card}
              </Tooltip.Trigger>
              <Tooltip.Content>Turn on Reputation Manager to design review posts</Tooltip.Content>
            </Tooltip>
          );
        }
        return <React.Fragment key={option.value}>{card}</React.Fragment>;
      })}
    </RadioGroup>
    <p className="text-xs text-slate-500">Either way, it's personalized for each player who shares it.</p>
  </div>
);
